import { getFileMetadataFromGCS } from './files-utils'
import { store } from '@/store/store'
import * as Sentry from '@sentry/react'
import { FileProcessingStatus, setFileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { determineGcsFileStatus } from './gcs-file-metadata-logic'
import { GCSFileMetadataStatusIndicator } from './gcs-file-self-assessment'

/**
 * Polls for GCS processing status until SUCCESS or ERROR.
 * Calls the callback with true (success) or false (error).
 */
export async function pollForFileStatusUntilSuccess(sourcePath: string, onComplete: (success: boolean) => void) {
  try {
    // Check the file status
    const fileMetadata = await getFileMetadataFromGCS(sourcePath)
    const fileStatus = determineGcsFileStatus({ fileMetadata })

    // If the file status is error, set the file status to error, and stop polling
    if (fileStatus.status === GCSFileMetadataStatusIndicator.ERROR) {
      store.dispatch(setFileProcessingStatus({ sourcePath, status: FileProcessingStatus.ERROR }))
      onComplete(false)
      return
    }

    // If the file status is done, set the file status to done, and stop polling
    if (fileStatus.status === GCSFileMetadataStatusIndicator.DONE) {
      store.dispatch(setFileProcessingStatus({ sourcePath, status: FileProcessingStatus.SUCCESS }))
      onComplete(true)
      return
    }

    // If the file is still processing, keep polling (recursive)
    store.dispatch(setFileProcessingStatus({ sourcePath, status: FileProcessingStatus.PROCESSING }))
    await new Promise((resolve) => setTimeout(resolve, 3000))
    await pollForFileStatusUntilSuccess(sourcePath, onComplete)
  } catch (error) {
    store.dispatch(setFileProcessingStatus({ sourcePath, status: FileProcessingStatus.ERROR }))

    Sentry.captureException(new Error(`Failed to poll for file's processing status`), {
      extra: {
        error: JSON.stringify(error),
      },
    })

    // Return here to stop the recursive polling
    return
  }
}
